type ChunkUploadCallback = (uploadedBytes?: number) => void;

interface ResumableUpload {
  uploadChunk: (chunk: Blob, onComplete?: ChunkUploadCallback) => Promise<void>;
  finalize: () => Promise<void>;
  finalizeSync: () => void;
  waitForUploads: () => Promise<void>;
  getSessionUri: () => string | null;
  getSessionUriPromise: () => Promise<string>;
  hasError: () => boolean;
  getConfirmedBytes: () => number;
  getPendingBytes: () => number;
  setRecordingStopped: () => void;
  // Non-terminal "stuck" signal: wall-clock cap tripped or offline wait exceeded.
  // When stuck, the upload is paused but state is preserved; call retryStuck()
  // to resume. hasError() returns false in this state.
  isStuck: () => boolean;
  retryStuck: () => Promise<void>;
}

const DEFAULT_CONTENT_TYPE = 'video/webm';
// GCS requires non-final resumable chunks to be 256 KiB multiples; all sizes below are aligned.
const MIN_CHUNK_SIZE = 256 * 1024; // 256 KiB - GCS floor / most reliable PUT
const MAX_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MiB - caps bytes-at-risk and per-PUT duration
const START_CHUNK_SIZE = 256 * 1024; // 256 KiB - start at the floor; grow on a healthy link
// Adaptive sizing: grow when a PUT is fast (fewer requests -> fewer 5xx dice-rolls), shrink when slow.
const CHUNK_GROW_BELOW_MS = 2_000;
const CHUNK_SHRINK_ABOVE_MS = 6_000;
const GROW_COOLDOWN_SUCCESSES = 3; // a failure re-arms this; blocks regrowth for N clean PUTs
const NO_PROGRESS_STALL_MS = 120_000; // 120s with zero confirmed-byte progress -> non-terminal stall
const FETCH_ATTEMPT_TIMEOUT_MS = 60_000; // per-PUT timeout so a hung request can't wedge finalization
// Chromium caps the combined body of all in-flight keepalive fetches at 64 KiB;
// a larger keepalive PUT is silently dropped, so finalizeSync must not send — nor
// optimistically confirm — a buffered tail above this limit.
const MAX_KEEPALIVE_BODY_SIZE = 64 * 1024; // 64 KiB

/**
 * Creates a GCS resumable upload handler following Google Cloud Storage best practices.
 * Reference: https://docs.cloud.google.com/storage/docs/performing-resumable-uploads
 *
 * Key behaviors:
 * - Handles 503/5xx by querying status and resuming from the confirmed offset
 * - Properly slices chunk data when resuming interrupted uploads
 * - Exponential backoff with jitter for retries
 * - Tracks GCS-confirmed bytes for accurate duration reporting
 * - 4xx is treated as a session-terminal signal: query status, accept a
 *   completed session as success, and otherwise treat it as unrecoverable
 *   for this session.
 * - Single-flusher mutex: only one PUT runs at a time; concurrent flush
 *   triggers are coalesced via `pendingFlush`.
 * - Weak-network resilience (no persistence): retries as long as bytes keep
 *   advancing. A slow-but-progressing upload never gives up. Only a genuine
 *   stall — zero confirmed-byte progress for NO_PROGRESS_STALL_MS, or an
 *   extended offline pause — flips the non-terminal `isStuck` state (buffer,
 *   sessionUrl, confirmedOffset preserved). Each PUT is bounded by
 *   FETCH_ATTEMPT_TIMEOUT_MS so a hung connection can't wedge it. Call
 *   `retryStuck()` to resume (e.g. from a user-facing Retry button).
 * - Never throws.
 *
 * Chunk lifecycle:
 *
 *   MediaRecorder ─► uploadChunk(blob)
 *                     │
 *                     ▼
 *                 chunkQueue (ordered, serialized blob→Uint8Array)
 *                     │
 *                     ▼
 *                 buffer[] (RAM)
 *                     │
 *                     ▼
 *                 flushBuffer ─► uploadBufferContents ─► PUT sessionUrl
 *                     │              (single-flusher mutex)
 *                     ▼
 *               confirmedOffset advances; notify callbacks
 *
 *   On finalize: drain queue → flushBufferFinal (adaptive chunks, partial OK
 *   only for the final PUT) → PUT Content-Range: bytes * / N.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Bounds a single request so a hung connection (dead but not offline)
// can't wedge the upload — the abort surfaces as a rejected fetch, which
// the retry loop treats as a failed attempt.
async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function getBackoffTime(attempt: number): number {
  const baseDelay = 1000;
  const maxDelay = 32000;
  const exponentialDelay = Math.min(
    baseDelay * Math.pow(2, attempt - 1),
    maxDelay
  );
  const jitter = Math.random() * 1000;
  return exponentialDelay + jitter;
}

// Waits for the browser to come back online so we don't burn retries against
// known-dead fetches. Bounded by `maxWaitMs` (the remaining no-progress
// window) so an offline stretch surfaces the stall/Retry on the same 120s
// contract rather than freezing the bar for minutes. Returns true if online
// (either already, or reconnected during the wait).
async function waitForOnline(maxWaitMs: number): Promise<boolean> {
  if (
    typeof navigator === 'undefined' ||
    navigator.onLine === undefined ||
    navigator.onLine
  ) {
    return true;
  }
  return new Promise<boolean>((resolve) => {
    let done = false;
    const finish = (online: boolean) => {
      if (done) return;
      done = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
      clearTimeout(timer);
      resolve(online);
    };
    const onOnline = () => finish(true);
    const timer = setTimeout(() => finish(false), maxWaitMs);
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    } else {
      finish(true);
    }
  });
}

export function createResumableUpload(
  signedUrl: string,
  options: {
    startChunkSize?: number;
    minChunkSize?: number;
    maxChunkSize?: number;
    contentType?: string;
  } = {}
): ResumableUpload {
  const contentType = options.contentType ?? DEFAULT_CONTENT_TYPE;
  // Align the bounds to 256 KiB multiples so adaptive sizing can never emit a
  // non-final PUT that violates GCS's multiple-of-256 KiB rule, whatever a
  // caller passes.
  const alignChunk = (bytes: number): number =>
    Math.max(
      MIN_CHUNK_SIZE,
      Math.floor(bytes / MIN_CHUNK_SIZE) * MIN_CHUNK_SIZE
    );
  const minChunkSize = alignChunk(options.minChunkSize ?? MIN_CHUNK_SIZE);
  const maxChunkSize = alignChunk(options.maxChunkSize ?? MAX_CHUNK_SIZE);

  let sessionUrl: string | null = null;
  let confirmedOffset = 0;
  // Timestamp of the last confirmed-byte advance. Drives the no-progress
  // stall detector: slow-but-progressing uploads keep resetting this, so
  // only a genuine stall (zero bytes for NO_PROGRESS_STALL_MS) trips isStuck.
  let lastProgressAt = Date.now();
  let isFinalized = false;
  const buffer: Uint8Array<ArrayBuffer>[] = [];
  let bufferSize = 0;
  let sessionUriResolvers: Array<(uri: string) => void> = [];

  let sessionClosed = false;
  let hasEncounteredError = false;
  // Non-terminal stall state. Set when there's been zero confirmed-byte
  // progress for NO_PROGRESS_STALL_MS (or an extended offline wait). Cleared
  // by retryStuck(). While stalled: buffer/confirmedOffset/sessionUrl are
  // preserved; finalize() does NOT mark isFinalized; hasEncounteredError
  // stays false.
  let isStuck = false;

  // Track pending operations for proper finalization
  const pendingChunkPromises: Promise<void>[] = [];
  let currentUploadPromise: Promise<void> | null = null;
  let isFlushingFinal = false;
  // Single-flusher mutex: concurrent flushBuffer() callers mark pendingFlush=true
  // and return; the owning caller re-checks the buffer after each PUT completes
  // and loops until no more work. Prevents concurrent uploadBufferContents entries
  // from racing on buffer/bufferSize/confirmedOffset.
  let pendingFlush = false;

  // Flag to indicate recording has stopped - freezes chunk-size growth (tail only)
  let recordingStopped = false;

  // Adaptive chunk size (256 KiB-aligned, clamped to [minChunkSize,maxChunkSize]).
  // Grows on fast PUTs, shrinks on slow PUTs or retryable failures. See adjustChunkSizeOn*.
  let currentChunkSize = alignChunk(options.startChunkSize ?? START_CHUNK_SIZE);
  let growCooldown = 0;

  // Queue for serializing chunk processing
  // This prevents race condition where chunks are pushed to buffer out of order
  // due to variable arrayBuffer() conversion times
  interface QueuedChunk {
    chunk: Blob;
    onComplete?: ChunkUploadCallback;
  }
  const chunkQueue: QueuedChunk[] = [];
  let isProcessingChunkQueue = false;

  function snapChunkSize(bytes: number): number {
    return Math.min(maxChunkSize, Math.max(minChunkSize, alignChunk(bytes)));
  }

  function adjustChunkSizeOnSuccess(putMs: number): void {
    if (putMs > CHUNK_SHRINK_ABOVE_MS) {
      currentChunkSize = snapChunkSize(currentChunkSize / 2);
      return;
    }
    // After recording stops we only drain the tail — hold/shrink, never gamble a
    // bigger PUT on the final bytes.
    if (recordingStopped) return;
    if (growCooldown > 0) {
      growCooldown--;
      return;
    }
    if (putMs < CHUNK_GROW_BELOW_MS) {
      currentChunkSize = snapChunkSize(currentChunkSize * 2);
    }
  }

  function adjustChunkSizeOnFailure(): void {
    currentChunkSize = snapChunkSize(currentChunkSize / 2);
    growCooldown = GROW_COOLDOWN_SUCCESSES;
  }
  async function queryUploadStatus(): Promise<{
    offset: number;
    isComplete: boolean;
  }> {
    if (!sessionUrl) return { offset: 0, isComplete: false };

    try {
      const res = await fetchWithTimeout(
        sessionUrl,
        {
          method: 'PUT',
          headers: {
            'Content-Length': '0',
            'Content-Range': 'bytes */*',
          },
        },
        FETCH_ATTEMPT_TIMEOUT_MS
      );

      if (res.status === 308) {
        const range = res.headers.get('Range');
        if (range) {
          const match = range.match(/bytes=(\d+)-(\d+)/);
          if (match) {
            const persistedUpTo = parseInt(match[2], 10);
            return { offset: persistedUpTo + 1, isComplete: false };
          }
        }
        return { offset: 0, isComplete: false };
      } else if (res.ok) {
        const storedLength = res.headers.get('x-goog-stored-content-length');
        const finalSize = storedLength
          ? parseInt(storedLength, 10)
          : confirmedOffset;
        return { offset: finalSize, isComplete: true };
      }

      return { offset: confirmedOffset, isComplete: false };
    } catch {
      return { offset: confirmedOffset, isComplete: false };
    }
  }

  interface PendingMediaChunk {
    chunkBytes: number;
    uploadedBytes: number;
    callback: ChunkUploadCallback;
  }
  const pendingMediaChunks: PendingMediaChunk[] = [];

  function notifyConfirmedBytes(bytesToNotify: number) {
    let remainingBytes = bytesToNotify;
    while (remainingBytes > 0 && pendingMediaChunks.length > 0) {
      const oldest = pendingMediaChunks[0];
      const toAttribute = Math.min(
        remainingBytes,
        oldest.chunkBytes - oldest.uploadedBytes
      );

      oldest.uploadedBytes += toAttribute;
      remainingBytes -= toAttribute;

      if (oldest.uploadedBytes >= oldest.chunkBytes) {
        oldest.callback(oldest.chunkBytes);
        pendingMediaChunks.shift();
      }
    }
  }

  async function uploadSingleChunk(
    chunkData: Uint8Array<ArrayBuffer>,
    chunkStartOffset: number,
    isFinal: boolean
  ): Promise<{
    success: boolean;
    newConfirmedOffset: number;
    sessionComplete: boolean;
    unrecoverableError: boolean;
    stuck?: boolean;
  }> {
    let attempt = 0;

    while (true) {
      attempt++;
      if (sessionClosed || hasEncounteredError || isStuck) {
        return {
          success: false,
          newConfirmedOffset: confirmedOffset,
          sessionComplete: sessionClosed,
          unrecoverableError: hasEncounteredError,
          stuck: isStuck,
        };
      }

      // Non-terminal stall: zero confirmed-byte progress for
      // NO_PROGRESS_STALL_MS. Slow-but-progressing uploads keep resetting
      // lastProgressAt, so this only trips on a genuine stall (dead/wedged
      // connection), not on a merely slow one.
      if (Date.now() - lastProgressAt > NO_PROGRESS_STALL_MS) {
        isStuck = true;
        return {
          success: false,
          newConfirmedOffset: confirmedOffset,
          sessionComplete: false,
          unrecoverableError: false,
          stuck: true,
        };
      }

      // Offline-aware: pause retries while we're known-offline so we don't
      // burn attempts against "Failed to fetch". Resets attempt counter on
      // reconnect so a transient tunnel doesn't eat the retry budget.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const remaining = Math.max(
          0,
          NO_PROGRESS_STALL_MS - (Date.now() - lastProgressAt)
        );
        const cameBack = await waitForOnline(remaining);
        if (!cameBack) {
          isStuck = true;
          return {
            success: false,
            newConfirmedOffset: confirmedOffset,
            sessionComplete: false,
            unrecoverableError: false,
            stuck: true,
          };
        }
        attempt = 1;
      }

      const dataStartInChunk = Math.max(0, confirmedOffset - chunkStartOffset);

      if (dataStartInChunk >= chunkData.length) {
        return {
          success: true,
          newConfirmedOffset: confirmedOffset,
          sessionComplete: false,
          unrecoverableError: false,
        };
      }

      const dataToSend =
        dataStartInChunk > 0 ? chunkData.slice(dataStartInChunk) : chunkData;

      const uploadStart = chunkStartOffset + dataStartInChunk;
      const uploadEnd = chunkStartOffset + chunkData.length - 1;
      const totalSize = isFinal ? (uploadEnd + 1).toString() : '*';
      const contentRange = `bytes ${uploadStart}-${uploadEnd}/${totalSize}`;

      try {
        if (!sessionUrl) {
          return {
            success: false,
            newConfirmedOffset: confirmedOffset,
            sessionComplete: false,
            unrecoverableError: true,
          };
        }

        const dataBlob = new Blob([dataToSend], { type: contentType });

        const putStart = Date.now();
        const res = await fetchWithTimeout(
          sessionUrl,
          {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
              'Content-Range': contentRange,
            },
            body: dataBlob,
          },
          FETCH_ATTEMPT_TIMEOUT_MS
        );
        const putMs = Date.now() - putStart;

        if (res.status === 308) {
          adjustChunkSizeOnSuccess(putMs);
          const range = res.headers.get('Range');
          if (range) {
            const match = range.match(/bytes=(\d+)-(\d+)/);
            if (match) {
              const newOffset = parseInt(match[2], 10) + 1;
              return {
                success: true,
                newConfirmedOffset: newOffset,
                sessionComplete: false,
                unrecoverableError: false,
              };
            }
          }
          return {
            success: true,
            newConfirmedOffset: uploadEnd + 1,
            sessionComplete: false,
            unrecoverableError: false,
          };
        }

        if (res.ok) {
          const storedLength = res.headers.get('x-goog-stored-content-length');
          const actualBytes = storedLength
            ? parseInt(storedLength, 10)
            : uploadEnd + 1;

          if (isFinal) {
            return {
              success: true,
              newConfirmedOffset: actualBytes,
              sessionComplete: true,
              unrecoverableError: false,
            };
          }

          // GCS finalized unexpectedly while we still expected more chunks.
          // CRITICAL: if we have any unsent data remaining (in our buffer,
          // in the chunk-conversion queue, or being processed) then GCS
          // closing the object NOW is data loss — the trailing bytes can
          // never be appended to a finalized GCS object. Surface it as an
          // unrecoverable error so callers do NOT claim success.
          //
          // Without this, finalize() bails on `sessionClosed=true` and the
          // onstop retry loop sees no error/stuck and declares the upload
          // done. Result: backend gets the full duration; the saved video
          // is whatever GCS happened to close at. This is the
          // "video stops mid-recording" bug.
          const haveUnsentData =
            bufferSize > 0 ||
            chunkQueue.length > 0 ||
            isProcessingChunkQueue ||
            pendingChunkPromises.length > 0;

          if (haveUnsentData) {
            hasEncounteredError = true;
            sessionClosed = true;
            return {
              success: false,
              newConfirmedOffset: actualBytes,
              sessionComplete: true,
              unrecoverableError: true,
            };
          }

          // No pending data — GCS finalized and we're truly done.
          // On first occurrence, verify with status check before accepting.
          if (attempt === 1) {
            await sleep(500);
            const status = await queryUploadStatus();

            if (status.isComplete) {
              sessionClosed = true;
              return {
                success: false,
                newConfirmedOffset: status.offset,
                sessionComplete: true,
                unrecoverableError: false,
              };
            } else {
              // Status says not complete but we got 200 - very unusual
              if (status.offset > confirmedOffset) {
                confirmedOffset = status.offset;
                lastProgressAt = Date.now();
              }
              continue;
            }
          }

          sessionClosed = true;
          return {
            success: false,
            newConfirmedOffset: actualBytes,
            sessionComplete: true,
            unrecoverableError: false,
          };
        }

        if (
          res.status === 503 ||
          res.status === 500 ||
          res.status === 502 ||
          res.status === 504
        ) {
          adjustChunkSizeOnFailure();
          // Standard exponential backoff (no doubled window). The per-chunk
          // wall-clock cap above bounds worst-case time; offline-aware pause
          // handles the "connection dead" case without burning retries.
          const backoffTime = getBackoffTime(attempt);
          await sleep(backoffTime);

          // Per GCS guidance: after a 5xx, query upload status before
          // retrying so we don't blindly re-PUT bytes that already landed
          // server-side. Re-PUTting an already-stored range can trigger
          // GCS to return 200 (treating the upload as final) on the
          // retry, which silently truncates the upload to the bytes
          // received so far. We learned this the hard way.
          const status = await queryUploadStatus();

          if (status.isComplete) {
            // Session is already finalized server-side. If we still
            // have unsent data, that's data loss — flag it.
            const haveUnsentData =
              bufferSize > 0 ||
              chunkQueue.length > 0 ||
              isProcessingChunkQueue ||
              pendingChunkPromises.length > 0 ||
              status.offset < chunkStartOffset + chunkData.length;

            if (haveUnsentData) {
              hasEncounteredError = true;
              sessionClosed = true;
              return {
                success: false,
                newConfirmedOffset: status.offset,
                sessionComplete: true,
                unrecoverableError: true,
              };
            }

            sessionClosed = true;
            return {
              success: false,
              newConfirmedOffset: status.offset,
              sessionComplete: true,
              unrecoverableError: false,
            };
          }

          // Advance to whatever GCS confirms; the next iteration's
          // `dataStartInChunk` math will skip already-stored bytes.
          if (status.offset > confirmedOffset) {
            confirmedOffset = status.offset;
            lastProgressAt = Date.now();
          }

          // Continue to next attempt (will re-upload with current confirmedOffset)
          continue;
        }

        if (res.status >= 400 && res.status < 500) {
          const status = await queryUploadStatus();

          if (status.isComplete) {
            // GCS finalized the object. If we still hold unsent bytes, that's
            // data loss (same guard as the 200/5xx paths) — surface it as an
            // error so the caller never reports false success on a truncation.
            const haveUnsentData =
              bufferSize > 0 ||
              chunkQueue.length > 0 ||
              isProcessingChunkQueue ||
              pendingChunkPromises.length > 0 ||
              status.offset < chunkStartOffset + chunkData.length;

            if (haveUnsentData) {
              hasEncounteredError = true;
              sessionClosed = true;
              return {
                success: false,
                newConfirmedOffset: status.offset,
                sessionComplete: true,
                unrecoverableError: true,
              };
            }

            sessionClosed = true;
            return {
              success: false,
              newConfirmedOffset: status.offset,
              sessionComplete: true,
              unrecoverableError: false,
            };
          }

          hasEncounteredError = true;
          return {
            success: false,
            newConfirmedOffset: status.offset,
            sessionComplete: false,
            unrecoverableError: true,
          };
        }

        hasEncounteredError = true;
        return {
          success: false,
          newConfirmedOffset: confirmedOffset,
          sessionComplete: false,
          unrecoverableError: true,
        };
      } catch {
        adjustChunkSizeOnFailure();
        // Network error / aborted (timeout) / offline blip. Query status so a
        // partial that landed server-side advances confirmedOffset (which
        // resets the no-progress stall timer), then back off and retry. The
        // loop is bounded by the no-progress stall check at the top.
        const status = await queryUploadStatus();
        if (status.isComplete) {
          // GCS finalized during the aborted/failed request. Unsent bytes here
          // are data loss — same guard as the 200/5xx paths — so don't report
          // a truncated object as success.
          const haveUnsentData =
            bufferSize > 0 ||
            chunkQueue.length > 0 ||
            isProcessingChunkQueue ||
            pendingChunkPromises.length > 0 ||
            status.offset < chunkStartOffset + chunkData.length;

          if (haveUnsentData) {
            hasEncounteredError = true;
            sessionClosed = true;
            return {
              success: false,
              newConfirmedOffset: status.offset,
              sessionComplete: true,
              unrecoverableError: true,
            };
          }

          sessionClosed = true;
          return {
            success: false,
            newConfirmedOffset: status.offset,
            sessionComplete: true,
            unrecoverableError: false,
          };
        }
        if (status.offset > confirmedOffset) {
          confirmedOffset = status.offset;
          lastProgressAt = Date.now();
        }
        await sleep(getBackoffTime(attempt));
      }
    }
  }

  async function initiateSession(): Promise<boolean> {
    if (sessionUrl) return true;

    try {
      const res = await fetch(signedUrl, {
        method: 'POST',
        headers: {
          'x-goog-resumable': 'start',
          'Content-Type': contentType,
        },
      });

      if (!res.ok) {
        hasEncounteredError = true;
        return false;
      }

      const location = res.headers.get('Location');
      if (!location) {
        hasEncounteredError = true;
        return false;
      }

      sessionUrl = location;

      sessionUriResolvers.forEach((resolve) => resolve(location));
      sessionUriResolvers = [];
      return true;
    } catch {
      hasEncounteredError = true;
      return false;
    }
  }

  // Core upload loop - processes buffer until empty, error, or stuck
  async function uploadBufferContents(isFinalFlush: boolean): Promise<void> {
    if (sessionClosed || hasEncounteredError || isStuck) return;

    // Ensure session is initiated
    if (!sessionUrl && bufferSize > 0) {
      const initiated = await initiateSession();
      if (!initiated) return;
    }

    while (
      bufferSize > 0 &&
      !sessionClosed &&
      !hasEncounteredError &&
      !isStuck
    ) {
      // Adaptive chunk size (see adjustChunkSizeOn*). 256 KiB-aligned, so every
      // non-final PUT satisfies GCS's multiple-of-256 KiB rule.
      const chunkSize = currentChunkSize;

      // For non-final flush, only upload complete chunks
      // For final flush, upload everything including partial chunks
      if (!isFinalFlush && bufferSize < chunkSize) {
        break;
      }

      const toUploadSize =
        isFinalFlush && bufferSize < chunkSize ? bufferSize : chunkSize;

      const toUpload = new Uint8Array(toUploadSize);
      let written = 0;

      while (written < toUploadSize && buffer.length > 0) {
        const first = buffer[0];
        const toCopy = Math.min(first.length, toUploadSize - written);
        toUpload.set(first.subarray(0, toCopy), written);
        written += toCopy;
        if (toCopy < first.length) {
          buffer[0] = first.subarray(toCopy);
        } else {
          buffer.shift();
        }
      }
      bufferSize -= toUploadSize;

      // Determine if this is the ACTUAL final chunk
      // Only mark as final if we're in final flush mode AND buffer is now empty
      const isLastChunk = isFinalFlush && bufferSize === 0;
      const chunkStartOffset = confirmedOffset;

      const result = await uploadSingleChunk(
        toUpload,
        chunkStartOffset,
        isLastChunk
      );

      if (result.stuck) {
        // Non-terminal: restore only the still-unconfirmed tail. confirmedOffset
        // may have advanced mid-chunk (5xx/error status queries), so re-buffering
        // the whole chunk would re-PUT already-stored bytes and corrupt the file.
        const consumed = Math.max(0, confirmedOffset - chunkStartOffset);
        const rest = consumed > 0 ? toUpload.subarray(consumed) : toUpload;
        buffer.unshift(rest);
        bufferSize += rest.length;
        break;
      }

      if (result.unrecoverableError) {
        hasEncounteredError = true;
        break;
      }

      if (result.sessionComplete) {
        sessionClosed = true;
        const bytesJustConfirmed = result.newConfirmedOffset - confirmedOffset;
        if (bytesJustConfirmed > 0) {
          notifyConfirmedBytes(bytesJustConfirmed);
          lastProgressAt = Date.now();
        }
        confirmedOffset = result.newConfirmedOffset;
        break;
      }

      if (result.success) {
        const bytesConfirmedThisChunk =
          result.newConfirmedOffset - confirmedOffset;
        if (bytesConfirmedThisChunk > 0) {
          notifyConfirmedBytes(bytesConfirmedThisChunk);
          lastProgressAt = Date.now();
        }
        confirmedOffset = result.newConfirmedOffset;
      } else {
        // Upload failed - restore only the unconfirmed tail (see stuck branch:
        // confirmedOffset may have advanced mid-chunk; re-PUTting stored bytes
        // would corrupt the file).
        const consumed = Math.max(0, confirmedOffset - chunkStartOffset);
        const rest = consumed > 0 ? toUpload.subarray(consumed) : toUpload;
        buffer.unshift(rest);
        bufferSize += rest.length;
        break;
      }
    }
  }

  // Non-blocking flush for regular chunk uploads.
  // Single-flusher: at most one uploadBufferContents(false) runs at a time.
  // If called while a flush is in progress, mark pendingFlush=true so the owning
  // caller re-checks the buffer when the current PUT resolves. This keeps the
  // buffer drained without letting two flushers race on shared state.
  async function flushBuffer(): Promise<void> {
    if (sessionClosed || hasEncounteredError || isFlushingFinal || isStuck)
      return;

    if (currentUploadPromise) {
      pendingFlush = true;
      return;
    }

    do {
      pendingFlush = false;

      if (sessionClosed || hasEncounteredError || isFlushingFinal || isStuck)
        return;

      // Only flush once a full adaptive chunk has accumulated. currentChunkSize
      // is 256 KiB-aligned, so non-final PUTs satisfy GCS's multiple-of-256 KiB
      // rule; sub-256 KiB partials only go out via flushBufferFinal.
      if (bufferSize < currentChunkSize) return;

      currentUploadPromise = uploadBufferContents(false);
      try {
        await currentUploadPromise;
      } finally {
        currentUploadPromise = null;
      }
    } while (pendingFlush);
  }

  // Blocking final flush - ensures ALL data is uploaded
  async function flushBufferFinal(): Promise<void> {
    if (sessionClosed || hasEncounteredError) return;

    isFlushingFinal = true;

    try {
      // Wait for any current upload to complete
      if (currentUploadPromise) {
        await currentUploadPromise;
        currentUploadPromise = null;
      }

      // Keep flushing until buffer is empty
      // This loop handles the case where new data arrives during flush
      let attempts = 0;
      const maxAttempts = 10;

      while (
        bufferSize > 0 &&
        !sessionClosed &&
        !hasEncounteredError &&
        attempts < maxAttempts
      ) {
        await uploadBufferContents(true);
        attempts++;

        // Small delay to catch any late-arriving chunks
        if (bufferSize === 0 && pendingChunkPromises.length > 0) {
          await sleep(100);
        }
      }
    } finally {
      isFlushingFinal = false;
    }
  }

  /**
   * Process chunks from the queue sequentially to maintain order.
   * This prevents race condition where chunks are pushed to buffer out of order
   * due to variable arrayBuffer() conversion times (larger chunks take longer).
   */
  async function processChunkQueue(): Promise<void> {
    if (isProcessingChunkQueue) return;
    isProcessingChunkQueue = true;

    try {
      while (chunkQueue.length > 0) {
        if (isFinalized || sessionClosed || hasEncounteredError) break;

        const queuedItem = chunkQueue.shift();
        if (!queuedItem) break;

        const { chunk, onComplete } = queuedItem;

        // Convert blob to array buffer - this is the async operation that was
        // causing race conditions when done in parallel
        const arr = new Uint8Array(await chunk.arrayBuffer());

        if (onComplete) {
          pendingMediaChunks.push({
            chunkBytes: arr.length,
            uploadedBytes: 0,
            callback: onComplete,
          });
        }

        // Data entering an empty buffer restarts the no-progress window, so
        // idle time (welcome modal, a mid-session pause) with nothing to send
        // never counts toward a stall.
        if (bufferSize === 0) lastProgressAt = Date.now();

        // Push to buffer IN ORDER (guaranteed by sequential processing)
        buffer.push(arr);
        bufferSize += arr.length;
      }
    } finally {
      isProcessingChunkQueue = false;
    }
  }

  async function uploadChunk(
    chunk: Blob,
    onComplete?: ChunkUploadCallback
  ): Promise<void> {
    if (isFinalized || sessionClosed || hasEncounteredError) return;

    // Add to queue (preserves arrival order)
    chunkQueue.push({ chunk, onComplete });

    // Track this chunk processing as a pending operation
    const chunkPromise = (async () => {
      // Process the queue - will process this chunk and any others waiting
      await processChunkQueue();
    })();

    // Track pending chunk promises for finalization
    pendingChunkPromises.push(chunkPromise);

    try {
      await chunkPromise;
    } finally {
      // Remove from pending
      const idx = pendingChunkPromises.indexOf(chunkPromise);
      if (idx >= 0) {
        void pendingChunkPromises.splice(idx, 1);
      }
    }

    // Trigger flush (non-blocking if upload already in progress)
    await flushBuffer();
  }

  async function finalize(): Promise<void> {
    if (isFinalized) {
      return;
    }

    // Step 1: Process any remaining chunks in the queue
    // This ensures all queued chunks are converted and added to buffer in order
    if (chunkQueue.length > 0) {
      await processChunkQueue();
    }

    // Step 2: Wait for all pending chunk processing to complete
    // This ensures any chunks that are being converted from Blob are in the buffer
    if (pendingChunkPromises.length > 0) {
      await Promise.all(pendingChunkPromises);
    }

    // Step 3: Small delay to catch any very late chunks
    // MediaRecorder's final ondataavailable can fire very close to onstop
    await sleep(200);

    // Step 4: Process any late-arriving chunks and wait for pending
    if (chunkQueue.length > 0) {
      await processChunkQueue();
    }
    if (pendingChunkPromises.length > 0) {
      await Promise.all(pendingChunkPromises);
    }

    if (sessionClosed || hasEncounteredError) {
      isFinalized = true;
      return;
    }

    // If the upload is already stuck before we even start the final flush,
    // bail without marking finalized. Caller should observe isStuck() and
    // either prompt the user to retry or call retryStuck() directly.
    if (isStuck) {
      return;
    }

    // Step 5: Final flush - upload all remaining data
    if (bufferSize > 0) {
      await flushBufferFinal();
    }

    // Step 6: Wait for any current upload to complete
    if (currentUploadPromise) {
      await currentUploadPromise;
    }

    // Step 7: One more flush attempt in case data arrived during step 6
    if (bufferSize > 0 && !sessionClosed && !hasEncounteredError && !isStuck) {
      await flushBufferFinal();
    }

    // Stuck during tail flush: do NOT mark finalized. Buffer, sessionUrl,
    // and confirmedOffset are preserved for retryStuck().
    if (isStuck) {
      return;
    }

    // Step 8: Send finalization request to GCS
    if (sessionUrl && confirmedOffset > 0 && !sessionClosed) {
      try {
        const res = await fetchWithTimeout(
          sessionUrl,
          {
            method: 'PUT',
            headers: {
              'Content-Length': '0',
              'Content-Range': `bytes */${confirmedOffset}`,
            },
            keepalive: true,
          },
          FETCH_ATTEMPT_TIMEOUT_MS
        );

        if (res.ok) {
          sessionClosed = true;
        } else if (res.status === 308) {
          // Still incomplete - try to query what's missing
          const status = await queryUploadStatus();
          if (status.isComplete) {
            sessionClosed = true;
            confirmedOffset = status.offset;
          } else if (status.offset < confirmedOffset) {
            // GCS hasn't caught up yet - wait and retry finalization
            await sleep(500);
            const retryRes = await fetchWithTimeout(
              sessionUrl,
              {
                method: 'PUT',
                headers: {
                  'Content-Length': '0',
                  'Content-Range': `bytes */${confirmedOffset}`,
                },
                keepalive: true,
              },
              FETCH_ATTEMPT_TIMEOUT_MS
            );
            if (
              retryRes.ok ||
              retryRes.status === 200 ||
              retryRes.status === 201
            ) {
              sessionClosed = true;
            }
          }
        }
      } catch {
        // Finalization error - ignore
      }
    }

    isFinalized = true;
  }

  async function waitForUploads(): Promise<void> {
    // Wait for pending chunk processing
    if (pendingChunkPromises.length > 0) {
      await Promise.all(pendingChunkPromises);
    }

    // Wait for current upload
    if (currentUploadPromise) {
      await currentUploadPromise;
    }
  }

  function finalizeSync(): void {
    if (isFinalized || !sessionUrl) return;

    if (sessionClosed || hasEncounteredError) {
      isFinalized = true;
      return;
    }

    // Best effort: flush remaining buffer synchronously, but only within the
    // 64 KiB keepalive cap. Above it Chromium silently drops the request, so
    // sending it and advancing confirmedOffset would declare a total GCS never
    // received and leave the object unfinalizable.
    if (bufferSize > 0 && buffer.length > 0) {
      if (bufferSize <= MAX_KEEPALIVE_BODY_SIZE) {
        const remainingData = new Uint8Array(bufferSize);
        let written = 0;
        for (const chunk of buffer) {
          remainingData.set(chunk, written);
          written += chunk.length;
        }

        const end = confirmedOffset + bufferSize - 1;
        fetch(sessionUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
            'Content-Range': `bytes ${confirmedOffset}-${end}/${end + 1}`,
          },
          body: new Blob([remainingData], { type: contentType }),
          keepalive: true,
        }).catch(() => {
          // Ignore - best effort
        });

        confirmedOffset += bufferSize;
      } else {
        // Drop the untrusted tail and finalize at the confirmed offset — a valid,
        // slightly-truncated recording — rather than corrupting the upload.
        console.warn(
          `[gcs-resumable-upload] finalizeSync: ${bufferSize} buffered bytes exceed the 64 KiB keepalive limit; dropping the unflushed tail and finalizing at ${confirmedOffset} bytes.`
        );
      }
      buffer.length = 0;
      bufferSize = 0;
    }

    if (confirmedOffset > 0) {
      fetch(sessionUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': '0',
          'Content-Range': `bytes */${confirmedOffset}`,
        },
        keepalive: true,
      }).catch(() => {
        // Ignore - best effort
      });
    }

    isFinalized = true;
  }

  function getSessionUri(): string | null {
    return sessionUrl;
  }

  function getSessionUriPromise(): Promise<string> {
    if (sessionUrl) {
      return Promise.resolve(sessionUrl);
    }
    return new Promise((resolve) => {
      sessionUriResolvers.push(resolve);
    });
  }

  function hasError(): boolean {
    return hasEncounteredError;
  }

  function getConfirmedBytes(): number {
    return confirmedOffset;
  }

  function getPendingBytes(): number {
    return bufferSize;
  }

  /**
   * Call this when recording stops but before finalization. Freezes adaptive
   * chunk-size growth: the remaining buffer is only the tail, so we hold (or
   * shrink) rather than gamble a larger PUT on the final bytes.
   */
  function setRecordingStopped(): void {
    recordingStopped = true;
  }

  function isStuckFn(): boolean {
    return isStuck;
  }

  // Clear the non-terminal stall flag and re-drive the buffer. Safe to call
  // when not stalled (no-op). Preserves sessionUrl, confirmedOffset, and
  // buffer. Resets the no-progress window so a fresh retry gets a full
  // NO_PROGRESS_STALL_MS budget before it can stall again.
  async function retryStuck(): Promise<void> {
    if (!isStuck) {
      return;
    }
    if (hasEncounteredError || sessionClosed) {
      return;
    }
    isStuck = false;
    lastProgressAt = Date.now();
    await flushBuffer();
  }

  return {
    uploadChunk,
    finalize,
    finalizeSync,
    waitForUploads,
    getSessionUri,
    getSessionUriPromise,
    hasError,
    getConfirmedBytes,
    getPendingBytes,
    setRecordingStopped,
    isStuck: isStuckFn,
    retryStuck,
  };
}
